import Article from '../../smoothr-web-app-core/models/Article';
import ArticleCategory from '../../smoothr-web-app-core/models/ArticleCategory';
import ArticleGroup from '../../smoothr-web-app-core/models/ArticleGroup';
import Order from '../../smoothr-web-app-core/models/Order';
import Venue from '../../smoothr-web-app-core/models/Venue';
import {defaultsToArticleOption} from '../../smoothr-web-app-core/utils/utils';
import {OrderUtils} from '../../smoothr-web-app-core/utils/order-utils';

export interface ExtraSelection {
	article: Article;
	quantity: number;
}

export const EXTRAS_DEFAULT_QUANTITY = 1;
export const EXTRAS_MAX_QUANTITY = 99;

// category is identified by name, ids differ per venue
const EXTRAS_CATEGORY_NAMES = ['stäbchen & extras', 'chopsticks & extras'];
const CHOPSTICKS_NAME = 'stäbchen';

export function isExtrasCategory(category: ArticleCategory): boolean {
	return [category?.name?.de, category?.name?.en].some(name =>
		EXTRAS_CATEGORY_NAMES.includes(name?.trim().toLocaleLowerCase())
	);
}

export function findExtrasArticles(venue: Venue): Article[] {
	const category = venue?.articleCategories?.find(isExtrasCategory);
	return (category?.articles ?? []).filter(article => !article.hidden);
}

function isExtrasArticle(extras: Article[], articleGroup: ArticleGroup): boolean {
	return extras.some(article => article._id === articleGroup.article._id);
}

function toArticleGroup(order: Order, selection: ExtraSelection): ArticleGroup {
	const articleGroup = new ArticleGroup();
	articleGroup.article = selection.article;
	articleGroup.groups.push(
		...defaultsToArticleOption(selection.article, [], selection.article.defaults, order.type, order.preorder.type)
	);
	articleGroup.quantity = selection.quantity;
	return articleGroup;
}

export function buildExtrasSelection(venue: Venue, order: Order): ExtraSelection[] {
	const extras = findExtrasArticles(venue);
	const inOrder = order.orderedArticles.filter(articleGroup => isExtrasArticle(extras, articleGroup));
	return extras.map(article => {
		const ordered = inOrder.filter(articleGroup => articleGroup.article._id === article._id);
		return {
			article,
			quantity: inOrder.length > 0
				? ordered.reduce((sum, articleGroup) => sum + articleGroup.quantity, 0)
				: EXTRAS_DEFAULT_QUANTITY,
		};
	});
}

function orderedArticlesWithExtras(order: Order, selection: ExtraSelection[]): ArticleGroup[] {
	const extras = selection.map(it => it.article);
	return [
		...order.orderedArticles.filter(articleGroup => !isExtrasArticle(extras, articleGroup)),
		...selection.filter(it => it.quantity > 0).map(it => toArticleGroup(order, it)),
	];
}

export function extrasTotalPrice(order: Order, selection: ExtraSelection[]): number {
	const preview = {...order, orderedArticles: orderedArticlesWithExtras(order, selection)} as Order;
	return OrderUtils.orderTotalPrice(preview, true, true, null, true);
}

export function applyExtrasToOrder(order: Order, selection: ExtraSelection[]) {
	order.orderedArticles = orderedArticlesWithExtras(order, selection);
	const chopsticks = selection.find(it => it.article.name?.de?.trim().toLocaleLowerCase() === CHOPSTICKS_NAME);
	order.preorder.personCount = Math.max(chopsticks?.quantity ?? 0, 1);
}
